// API functions for lead management.
//
// Goes through the shared apiV1 client so every call carries the signed-in
// user's token (bare fetch() sent none, so authenticated lead routes 401'd).

import { apiV1 } from '@/api/client';

// Resolve to the response body like fetch().json() did, including for error
// statuses, so callers can keep checking `result.success`.
const body = (request) =>
  request
    .then((response) => response.data)
    .catch((error) => {
      if (error.response?.data) return error.response.data;
      throw error;
    });

export const leadsApi = {
  // List leads
  list(params = {}) {
    return body(apiV1.get('/leads/', { params }));
  },

  // Get single lead
  get(id) {
    return body(apiV1.get(`/leads/${id}`));
  },

  // Create lead
  create(data) {
    return body(apiV1.post('/leads/', data));
  },

  // Update lead
  update(id, data) {
    return body(apiV1.put(`/leads/${id}`, data));
  },

  // Delete lead
  delete(id) {
    return body(apiV1.delete(`/leads/${id}`));
  },

  // Change stage
  changeStage(id, stage) {
    return body(apiV1.post(`/leads/${id}/stage/${stage}`));
  },

  // Assign lead
  assign(id, assignedTo, assignedToName) {
    return body(apiV1.post(`/leads/${id}/assign`, { assigned_to: assignedTo, assigned_to_name: assignedToName }));
  },

  // Convert lead
  convert(id, value, notes) {
    return body(apiV1.post(`/leads/${id}/convert`, { conversion_value: value, notes }));
  },

  // Mark as lost
  markLost(id, reason) {
    return body(apiV1.post(`/leads/${id}/lost`, null, { params: { reason: reason || '' } }));
  },

  // Add activity
  addActivity(id, type, description, metadata = {}) {
    return body(apiV1.post(`/leads/${id}/activities`, { type, description, metadata }));
  },

  // Add task
  addTask(id, title, description, dueDate, assignedTo) {
    return body(apiV1.post(`/leads/${id}/tasks`, { title, description, due_date: dueDate, assigned_to: assignedTo }));
  },

  // Complete task
  completeTask(leadId, taskId) {
    return body(apiV1.post(`/leads/${leadId}/tasks/${taskId}/complete`));
  },

  // Get pipeline stats
  getStats(params = {}) {
    return body(apiV1.get('/leads/stats', { params }));
  },

  // Get stage options
  getStageOptions() {
    return body(apiV1.get('/leads/options/stages'));
  },

  // Get source options
  getSourceOptions() {
    return body(apiV1.get('/leads/options/sources'));
  },

  // Get priority options
  getPriorityOptions() {
    return body(apiV1.get('/leads/options/priorities'));
  },
};

export default leadsApi;
